import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithDefault, loadable } from 'jotai/utils';
import { IFinanceRoundDataModel } from '../../../data-models/finance-round.data-model';
import { IRoundReconDataModel } from '../../../data-models/finance-round-recon.data-model';
import { ITransactionDataModel } from '../../../data-models/transaction.data-model';
import {
  fetchAllInvestmentRounds,
  fetchRoundRecon,
  fetchTransactionsByCompany2,
} from '../../../services/queries/MaggieTransactionQueries';

type InvestmentRoundsValue = IFinanceRoundDataModel[] | Promise<IFinanceRoundDataModel[]>;
type TransactionsValue = ITransactionDataModel[] | Promise<ITransactionDataModel[]>;
type RoundReconValue = IRoundReconDataModel[] | Promise<IRoundReconDataModel[]>;

/**
 * Default fetches every investment round (across all companies) on first
 * subscribe. Refetched by {@link useInvalidateFinanceData.invalidateInvestments}.
 */
export const allInvestmentRoundsStateJ = atomWithDefault<InvestmentRoundsValue>(async () =>
  fetchAllInvestmentRounds()
);

export const allInvestmentRoundsStateLoadableJ = loadable(allInvestmentRoundsStateJ);

export const allInvestmentRoundsMapStateJ = atom(
  async (get): Promise<Map<number, IFinanceRoundDataModel>> => {
    const rounds = await get(allInvestmentRoundsStateJ);
    const map = new Map<number, IFinanceRoundDataModel>();
    rounds.forEach((r) => map.set(r.id, r));
    return map;
  }
);

export const roundsByCompanyIdStateJ = atomFamily((companyId: number) =>
  atom(async (get): Promise<IFinanceRoundDataModel[]> => {
    const all = await get(allInvestmentRoundsStateJ);
    return all.filter((r) => r.companyId === companyId);
  })
);

export const roundsByCompanyIdStateLoadableJ = atomFamily((companyId: number) =>
  loadable(roundsByCompanyIdStateJ(companyId))
);

export const companyRoundsByIdStateJ = atomFamily((companyId: number) =>
  atom(async (get): Promise<Map<number, IFinanceRoundDataModel>> => {
    const rounds = await get(roundsByCompanyIdStateJ(companyId));
    const map = new Map<number, IFinanceRoundDataModel>();
    rounds.forEach((r) => map.set(r.id, r));
    return map;
  })
);

/**
 * Sentinel-guarded: returns `[]` for non-positive ids so consumers using
 * the `?? -1` pattern (Transactions3, RoundTable, InvestmentSelectOrCreateForm)
 * don't fire a spurious `fetchTransactionsByCompany2(-1)` request.
 */
export const financeTransactionsByCompanyStateJ = atomFamily((companyId: number) =>
  atomWithDefault<TransactionsValue>(async () => {
    if (companyId <= 0) {
      return [];
    }
    return fetchTransactionsByCompany2(companyId);
  })
);

export const financeTransactionsByCompanyStateLoadableJ = atomFamily((companyId: number) =>
  loadable(financeTransactionsByCompanyStateJ(companyId))
);

export const transactionsByIdStateJ = atomFamily((companyId: number) =>
  atom(async (get): Promise<Map<string, ITransactionDataModel>> => {
    const transactions = await get(financeTransactionsByCompanyStateJ(companyId));
    const map = new Map<string, ITransactionDataModel>();
    transactions.forEach((t) => map.set(String(t.id), t));
    return map;
  })
);

export const roundReconStateJ = atomWithDefault<RoundReconValue>(async () => fetchRoundRecon());

export const roundReconStateLoadableJ = loadable(roundReconStateJ);

/**
 * Increment to invalidate the all-transactions-by-date query (read by
 * `AllTransactions.tsx`; written by `useInvalidateFinanceData.invalidateTransactions`).
 */
export const forceAllTransactionsByDateUpdateJ = atom<number>(0);
